import { COOKIES, SWR_KEYS } from '@bulknutrients/bulk-shared-lib/constants';
import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type { CustomCart } from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { log, logBreadcrumb } from '@lib/logs';
import Cookies from 'js-cookie';
import type { GetCartAPIResponse } from 'pages/api/cart';
import { useEffect } from 'react';
import useSWR from 'swr';

export function useCart(): {
  cart: CustomCart | undefined;
  error: Error | null | undefined;
  isLoading: boolean;
} {
  const apiUrl = new URL('/api/cart', 'https://a');
  apiUrl.searchParams.set('include', 'line_items.physical_items.options');

  const fetcher = async () => {
    const res = await fetch(apiUrl.pathname + apiUrl.search);

    if (!res.ok) {
      if (res.status === 400 || res.status === 401) {
        return undefined;
      }

      const error = new Error('Unable to fetch cart');
      (error as Error & { status?: number }).status = res.status;
      throw error;
    }

    const serverRes: GetCartAPIResponse = await res.json();

    if (isErrorResponse(serverRes)) {
      const {
        error: { message, code },
      } = serverRes;
      if (code === 404 || code === 400) {
        return undefined;
      }
      const error = new Error('Unable to fetch cart');
      error.message = message.join('. ');
      throw error;
    }

    return serverRes.data;
  };

  const hasCartCookie = Boolean(Cookies.get(COOKIES.cart));

  // Get cart
  const { data, error, isLoading } = useSWR<CustomCart | undefined>(
    hasCartCookie ? SWR_KEYS.cart : null,
    fetcher,
    {
      dedupingInterval: 30000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      onError: (error) => {
        logBreadcrumb({
          category: 'hooks',
          message: 'useCart request failed',
          level: 'error',
        });

        log({ error, location: 'useCart' });
      },
    }
  );

  useEffect(() => {
    logBreadcrumb({
      category: 'useCart',
      message: 'received new cart data from BigCommerce',
      level: 'info',
      data: data,
    });
  }, [data]);

  return { cart: data, error, isLoading };
}
